'use client'

import { TypedDocumentNode } from '@graphql-typed-document-node/core'
import {
  UseQueryOptions,
  UseQueryResult,
  useQuery,
} from '@tanstack/react-query'
import { gqlClient } from './useGraphQL.server'

const shouldDebug = () => {
  // Check NEXT_PUBLIC_ENV (used in docker) or NEXT_PUBLIC_VERCEL_ENV (used in Vercel)
  const env = process.env.NEXT_PUBLIC_ENV || process.env.NEXT_PUBLIC_VERCEL_ENV
  const nodeEnv = process.env.NODE_ENV
  
  // Always log once to help diagnose - this will show us what's actually available
  if (typeof window !== 'undefined' && !(window as any).__debugEnvChecked) {
    console.log('[DEBUG] Environment check:', { 
      NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
      NEXT_PUBLIC_VERCEL_ENV: process.env.NEXT_PUBLIC_VERCEL_ENV,
      nodeEnv,
      allNextPublicVars: Object.keys(process.env).filter(k => k.startsWith('NEXT_PUBLIC_'))
    })
    ;(window as any).__debugEnvChecked = true
  }
  
  // Check if we're in dev/staging
  return env === 'dev' || env === 'staging' || env === 'development' || env === 'preview' || nodeEnv === 'development'
}

export const useGQLQuery = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?:
    | (TVariables extends Record<string, never> ? {} : TVariables)
    | null,
  options?: UseQueryOptions<TResult, unknown, TResult>
): UseQueryResult<TResult> =>
  useQuery({
    queryKey: [
      (document.definitions?.[0] as any).name.value,
      variables ?? undefined,
    ],
    queryFn: async ({ queryKey }) => {
      const queryName = (document.definitions?.[0] as any).name.value
      const startTime = performance.now()
      if (shouldDebug()) {
        console.log(`[DEBUG] ${queryName}: Starting GraphQL request at ${new Date().toISOString()}`)
      }
      
      try {
        const result = await gqlClient().request(
          document,
          queryKey?.[1] ? queryKey[1] : undefined
        )
        const endTime = performance.now()
        if (shouldDebug()) {
          console.log(`[DEBUG] ${queryName}: Received response at ${new Date().toISOString()} (took ${(endTime - startTime).toFixed(2)}ms)`)
          console.log(`[DEBUG] ${queryName}: Response data keys:`, result ? Object.keys(result) : 'null')
        }
        return result
      } catch (error) {
        const endTime = performance.now()
        if (shouldDebug()) {
          console.error(`[DEBUG] ${queryName}: Request failed at ${new Date().toISOString()} (took ${(endTime - startTime).toFixed(2)}ms)`, error)
        }
        throw error
      }
    },
    ...options,
  })

export const useGQLSuspenseQuery = <TResult, TVariables>(
  document: TypedDocumentNode<TResult, TVariables>,
  options: UseQueryOptions<TResult, unknown, TResult> = {},
  ...[variables]: TVariables extends Record<string, never> ? [] : [TVariables]
): UseQueryResult<TResult> =>
  useQuery({
    queryKey: [(document.definitions?.[0] as any).name.value, variables],
    queryFn: async ({ queryKey }) => {
      const queryName = (document.definitions?.[0] as any).name.value
      const startTime = performance.now()
      if (shouldDebug()) {
        console.log(`[DEBUG] ${queryName}: Starting GraphQL request (suspense) at ${new Date().toISOString()}`)
      }
      
      try {
        const result = await gqlClient().request(
          document,
          queryKey?.[1] ? queryKey[1] : undefined
        )
        const endTime = performance.now()
        if (shouldDebug()) {
          console.log(`[DEBUG] ${queryName}: Received response (suspense) at ${new Date().toISOString()} (took ${(endTime - startTime).toFixed(2)}ms)`)
          console.log(`[DEBUG] ${queryName}: Response data keys:`, result ? Object.keys(result) : 'null')
        }
        return result
      } catch (error) {
        const endTime = performance.now()
        if (shouldDebug()) {
          console.error(`[DEBUG] ${queryName}: Request failed (suspense) at ${new Date().toISOString()} (took ${(endTime - startTime).toFixed(2)}ms)`, error)
        }
        throw error
      }
    },
    suspense: true,
    ...options,
  })
